import React from 'react'
import { Box, Typography } from '@mui/material'
import { useApplicationSuiteContext } from '../../../contexts/ApplicationSuiteProvider'
import { forceExitLabel } from '../../../common/utils/forceExit'

interface Row {
  label: string
  value: string
}

interface Group {
  title: string
  rows: Row[]
}

function buildGroups(alert: any): Group[] {
  const options = alert?.trading?.options
  if (!options) return []

  const gt = options.generateTrades
  const at = options.analyzeTrades

  const strategyLabel: Record<string, string> = {
    peak_valley: 'Fractal',
    high_low: 'High/Low',
    atr: 'ATR',
    incremental: 'Incremental',
    fixed: 'Fixed',
  }

  const rr = gt.tradeRatios?.map((r: any) => `${r.risk}/${r.reward}`).join('  ') ?? '—'

  const stopLossRows: Row[] = [
    { label: 'Strategy', value: strategyLabel[gt.exitStrategy] ?? gt.exitStrategy },
    { label: 'R/R', value: rr },
  ]

  if (gt.exitStrategy === 'atr') {
    stopLossRows.push({ label: 'ATR Period', value: `${gt.atrPeriod}` })
    stopLossRows.push({ label: 'ATR Chart', value: `${gt.atrChartPeriod} ${gt.atrChartTimeframe}` })
  }
  if (gt.exitStrategy === 'peak_valley') {
    stopLossRows.push({ label: 'Fractals', value: `${gt.fractalCount}` })
    stopLossRows.push({ label: 'Lookback', value: `${gt.maxPeakValleyLookback}` })
    stopLossRows.push({ label: 'Offset', value: `${gt.priceOffset}` })
  }
  if (gt.exitStrategy === 'high_low') {
    stopLossRows.push({ label: 'Lookback', value: `${gt.maxLookback}` })
    stopLossRows.push({ label: 'Offset', value: `${gt.priceOffset}` })
  }
  if (gt.exitStrategy === 'incremental') {
    if (gt.incrementalStart != null)
      stopLossRows.push({ label: 'Start', value: `${gt.incrementalStart}` })
    if (gt.incrementalStop != null)
      stopLossRows.push({ label: 'Stop', value: `${gt.incrementalStop}` })
    if (gt.incrementalStep != null)
      stopLossRows.push({ label: 'Step', value: `${gt.incrementalStep}` })
  }

  const accountRows: Row[] = [
    { label: 'Risk/Trade', value: `${(at.riskPerTrade * 100).toFixed(0)}%` },
    { label: 'Max Risk', value: at.maxRisk ? `${(at.maxRisk * 100).toFixed(0)}%` : '∞' },
    { label: 'Max Pos', value: at.tradeLimit ? `${at.tradeLimit}` : '∞' },
    { label: 'Daily', value: at.dailyTradeLimit ? `${at.dailyTradeLimit}` : '∞' },
  ]
  if (at.allowHedging) accountRows.push({ label: 'Hedging', value: 'On' })
  const forceExit = forceExitLabel(at)
  if (forceExit) accountRows.push({ label: 'Close After', value: forceExit })

  const groups: Group[] = [
    { title: 'Account', rows: accountRows },
    { title: 'Stop Loss', rows: stopLossRows },
  ]

  return groups
}

interface TradeParamsCompactProps {
  alert: any
}

const TradeParamsCompact: React.FC<TradeParamsCompactProps> = ({ alert }) => {
  const { usermeta } = useApplicationSuiteContext()
  const groups = buildGroups(alert)

  const connectedAccount = usermeta.metaapi?.accounts?.find(
    (acc: any) => acc.accountId === alert.trading?.accountId
  )
  if (connectedAccount) {
    const tradingAccountRows: Row[] = [
      { label: 'Account', value: connectedAccount.name },
      { label: 'Server', value: connectedAccount.server },
    ]
    if (alert.trading?.broker?.symbol) {
      tradingAccountRows.push({ label: 'Symbol', value: alert.trading.broker.symbol })
    }
    groups.push({ title: 'Trading Account', rows: tradingAccountRows })
  }

  if (!groups.length) return null

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      {groups.map((group, gi) => (
        <Box key={gi}>
          {/* Section header — hairline + uppercase label */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 0.75,
            }}
          >
            <Typography
              sx={{
                fontSize: '9px',
                fontWeight: 700,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: 'rgba(255,255,255,0.28)',
                whiteSpace: 'nowrap',
              }}
            >
              {group.title}
            </Typography>
            <Box
              sx={{
                flex: 1,
                height: '1px',
                backgroundColor: 'rgba(255,255,255,0.07)',
              }}
            />
          </Box>

          {/* 2-col grid of key: value pairs */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              rowGap: '4px',
              columnGap: '12px',
            }}
          >
            {group.rows.map((row, ri) => (
              <Box key={ri} sx={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: 'rgba(255,255,255,0.38)',
                    whiteSpace: 'nowrap',
                    lineHeight: 1.4,
                  }}
                >
                  {row.label}:
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'rgba(255,255,255,0.82)',
                    fontFamily: '"Roboto Mono", "Courier New", monospace',
                    lineHeight: 1.4,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {row.value}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  )
}

export default TradeParamsCompact
